// Unpublished Work © 2025-2026 Deere & Company.

import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'Utils/prop-type-utils';
import {Box, Button, Card, Grid, Stack, Stepper, Typography} from '@deere/isg.component-library';
import {IconAdd, IconCircleMinus} from '@deere/icons';
import WorkboardActionButtons from './workboard-action-buttons';
import {deleteSelectedOperators, removeSelectedJobs} from 'Src/ui-core/store/actions/setup-workboard';
import {getSelectedJobs, getSelectedOperators} from '../utils/setup-workboard';
import {DUPLICATE_ENTITY_TYPES} from '../utils/workboard-conflict-utils';
import {replaceTranslationNames} from 'Src/ui-core/utils/translation-utils';
import dialogTypes from '../../../common/dialog-types';
import {closeDialog, openDialog} from 'Src/ui-core/store/actions/dialogs';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {getOperatorFormattedName} from '../utils/operator-utils';
import MediaQuery, {MOBILE_MEDIA_QUERY} from 'Ui/components/higher-order-components/media-query';
import {EXISTING_WORKBOARD_TEMPLATE, NEW_WORKBOARD_TEMPLATE} from '../constants/workboard-creation-flows';

function getRemoveConfirmationMessage(groupByUser, counter, translations) {
    let body;

    if (groupByUser) {
        body = counter === 1 ? translations.OPERATOR : translations.OPERATORS;
    } else {
        body = counter === 1 ? translations.jobs_job : translations.JOBS;
    }

    return replaceTranslationNames(translations.ONLINK_REMOVE_ASSIGNMENT_DIALOG_CONFIRMATION, {
        '0': counter,
        '1': body.toLowerCase()
    });
}

function handleRemoveJobs(openConfirmation, closeConfirmation, jobsToRemove, jobs, selectedJobs, handleRemoveSelectedJob, addToast, translations, groupByUser) {
    openConfirmation({
        onCancel: closeConfirmation,
        showLoadingIcon: true,
        message: getRemoveConfirmationMessage(
            false, jobsToRemove.length, translations, groupByUser),
        title: replaceTranslationNames(translations.REMOVE_PLACEHOLDER, {
            '0': translations.JOBS
        }),
        cancelTitle: translations.CANCEL,
        continueTitle: translations.CONFIRM_DELETE,
        onContinue() {
            const jobsToRemoveNames = getSelectedJobs(jobs, jobsToRemove).map((job) => job.title).join(', ');

            try {
                handleRemoveSelectedJob({
                    idsToRemove: [...jobsToRemove],
                    jobs: [...selectedJobs]
                });
                addToast({
                    title: translations.SUCCESS,
                    message: replaceTranslationNames(translations.ONLINK_REMOVE_SUCCESS, {
                        '0': jobsToRemoveNames
                    }),
                    persist: false,
                    type: TOAST_TYPE.SUCCESS
                });
            } catch {
                addToast({
                    title: translations.ONLINK_REMOVE_JOB_ERROR,
                    message: replaceTranslationNames(translations.ONLINK_REMOVE_ASSIGNMENT_ERROR_TOAST, {
                        '0': jobsToRemoveNames
                    }),
                    persist: false,
                    type: TOAST_TYPE.ERROR
                });
            } finally {
                closeConfirmation();
            }
        }
    });
}

function handleRemoveOperators(openConfirmation, closeConfirmation, operatorsToRemove, operators, selectedOperators, handleRemoveSelectedOperator, addToast, translations, groupByUser) {
    openConfirmation({
        onCancel: closeConfirmation,
        showLoadingIcon: true,
        message: getRemoveConfirmationMessage(
            true, operatorsToRemove.length, translations, groupByUser),
        title: replaceTranslationNames(translations.REMOVE_PLACEHOLDER, {
            '0': translations.OPERATORS
        }),
        cancelTitle: translations.CANCEL,
        continueTitle: translations.CONFIRM_DELETE,
        onContinue() {
            const operatorsToRemoveNames = getSelectedOperators(operators, operatorsToRemove).map((operator) => getOperatorFormattedName(operator)).join(', ');

            try {
                handleRemoveSelectedOperator({
                    idsToRemove: [...operatorsToRemove],
                    operators: [...selectedOperators]
                });
                addToast({
                    title: translations.SUCCESS,
                    message: replaceTranslationNames(translations.ONLINK_REMOVE_SUCCESS, {
                        '0': operatorsToRemoveNames
                    }),
                    persist: false,
                    type: TOAST_TYPE.SUCCESS
                });
            } catch {
                addToast({
                    title: translations.ONLINK_REMOVE_OPERATOR_ERROR,
                    message: replaceTranslationNames(translations.ONLINK_REMOVE_ASSIGNMENT_ERROR_TOAST, {
                        '0': operatorsToRemoveNames
                    }),
                    persist: false,
                    type: TOAST_TYPE.ERROR
                });
            } finally {
                closeConfirmation();
            }
        }
    });
}

function WorkboardFormStepper(props) {
    const {
        translations,
        activeStep,
        groupByUser,
        handleRemoveSelectedJob,
        handleRemoveSelectedOperator,
        jobsToRemove,
        operatorsToRemove,
        jobs,
        selectedJobs,
        isMobile,
        setActiveStep,
        title,
        type,
        openConfirmation,
        closeConfirmation,
        openAddAssignmentsDialog,
        operators,
        selectedOperators,
        nameValidation
    } = props;

    const {addToast} = useToast();

    const steps = React.useMemo(() => {
        return [
            {
                name: activeStep > 0 && isMobile ? '' : translations.ONLINK_SETUP_WORKBOARD,
                completed: activeStep > 0
            },
            {
                name: activeStep < 1 && isMobile ? '' : translations.ONLINK_ASSIGN_WORKBOARD,
                completed: false
            }
        ];
    }, [activeStep, isMobile, translations]);

    const isTemplateFlow = type === EXISTING_WORKBOARD_TEMPLATE || type === NEW_WORKBOARD_TEMPLATE;
    const isTemplateDuplicateActive = isTemplateFlow &&
        Boolean(nameValidation?.isDuplicate) &&
        nameValidation?.duplicateEntityType === DUPLICATE_ENTITY_TYPES.TEMPLATE;

    const handleStep = (index) => {
        if (isTemplateDuplicateActive && index > 0) {
            return;
        }

        setActiveStep(index);
    };

    return (
        <Box
            display={'flex'}
            flexDirection={'column'}
            minHeight={'100%'}
            overflow={'hidden'}
        >
            <Card>
                <Box
                    p={'1rem'}
                    position={'sticky'}
                    top={0}
                    zIndex={10}
                >
                    <Grid
                        container
                        sx={{
                            flexDirection: {
                                xs: 'column',
                                md: 'row'
                            },
                            gap: {
                                xs: '1rem',
                                md: '0'
                            }
                        }}
                    >
                        <Grid
                            direction={'row'}
                            size={10}
                            sx={{
                                ...isMobile && {
                                    width: '100%'
                                }
                            }}
                        >
                            <Stack
                                alignItems={'center'}
                                direction={'row'}
                                display={'flex'}
                            >
                                <Typography
                                    color='textSecondary'
                                    sx={{
                                        ...isMobile && {
                                            padding: '12px'
                                        }
                                    }}
                                    variant='h3'
                                >{title}</Typography>
                                <Stepper
                                    activeStep={activeStep}
                                    handleStep={handleStep}
                                    steps={steps}
                                    sx={{
                                        width: '60%',
                                        padding: {
                                            xs: '0 !important',
                                            md: '24px !important'
                                        },
                                        ...isMobile && {
                                            flex: 1
                                        }
                                    }}
                                />
                            </Stack>
                        </Grid>
                        {
                            activeStep > 0 &&
                            <Grid
                                alignItems={'center'}
                                direction={'row'}
                                display={'flex'}
                                gap='16px'
                                size={2}
                                sx={{
                                    justifyContent: {
                                        xs: 'flex-start',
                                        md: 'flex-end'
                                    },
                                    ...isMobile && {
                                        paddingLeft: '12px'
                                    }
                                }}
                            >
                                <Button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        openAddAssignmentsDialog({
                                            addUser: groupByUser
                                        });
                                    }}
                                    startIcon={<IconAdd/>}

                                >
                                    {replaceTranslationNames(translations.ONLINK_ADD_ITEM, {
                                        '0': groupByUser ? translations.OPERATORS : translations.JOBS
                                    })}
                                </Button>
                                {!isMobile &&
                                    <Button
                                        disabled={groupByUser ? !operatorsToRemove.length : !jobsToRemove.length}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            groupByUser ? handleRemoveOperators(openConfirmation, closeConfirmation, operatorsToRemove, operators, selectedOperators, handleRemoveSelectedOperator, addToast, translations, groupByUser) :
                                                handleRemoveJobs(openConfirmation, closeConfirmation, jobsToRemove, jobs, selectedJobs, handleRemoveSelectedJob, addToast, translations, groupByUser);
                                        }}
                                        startIcon={<IconCircleMinus/>}
                                    >
                                        {replaceTranslationNames(translations.REMOVE_PLACEHOLDER, {
                                            '0': groupByUser ? translations.OPERATORS : translations.JOBS
                                        })}
                                    </Button>}
                            </Grid>
                        }
                    </Grid>
                </Box>
            </Card>
            <Box
                flexGrow={1}
                sx={{
                    overflowY: 'auto'
                }}
            >
                {props.children}
            </Box>
            <Card
                sx={{
                    position: 'fixed',
                    width: '100%',
                    bottom: 0,
                    borderTop: '1px solid #C7C6C5'
                }}
            >
                <WorkboardActionButtons
                    activeStep={activeStep}
                    setActiveStep={setActiveStep}
                    stepsLength={steps.length}
                    translations={translations}
                    type={type}
                />
            </Card>
        </Box>
    );
}

WorkboardFormStepper.propTypes = {
    activeStep: PropTypes.number,
    children: PropTypes.element,
    closeConfirmation: PropTypes.func,
    groupByUser: PropTypes.bool,
    handleRemoveSelectedJob: PropTypes.func,
    handleRemoveSelectedOperator: PropTypes.func,
    isMobile: PropTypes.bool,
    jobs: PropTypes.arrayOf(PropTypes.object),
    jobsToRemove: PropTypes.array,
    membership: PropTypes.membership,
    nameValidation: PropTypes.object,
    openAddAssignmentsDialog: PropTypes.func,
    openConfirmation: PropTypes.func,
    operators: PropTypes.arrayOf(PropTypes.object),
    operatorsToRemove: PropTypes.array,
    selectedJobs: PropTypes.arrayOf(PropTypes.object),
    selectedOperators: PropTypes.arrayOf(PropTypes.object),
    setActiveStep: PropTypes.func,
    title: PropTypes.string,
    translations: PropTypes.translations,
    type: PropTypes.string
};

export function mapStateToProps(state) {
    return {
        jobsToRemove: state.setupWorkboard.jobsToRemove,
        operatorsToRemove: state.setupWorkboard.operatorsToRemove,
        selectedJobs: state.setupWorkboard.jobs.selectedJobs,
        jobs: state.setupWorkboard.jobs.jobs,
        operators: state.setupWorkboard.operators.teamMembers,
        selectedOperators: state.setupWorkboard.operators.selected,
        membership: state.membership,
        nameValidation: state.setupWorkboard.nameValidation
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        handleRemoveSelectedJob(props) {
            dispatch(removeSelectedJobs(props));
        },
        handleRemoveSelectedOperator(props) {
            dispatch(deleteSelectedOperators(props));
        },
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        },
        openAddAssignmentsDialog(props) {
            dispatch(openDialog(dialogTypes.ADD_ASSIGNMENTS_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(MediaQuery(MOBILE_MEDIA_QUERY)(WorkboardFormStepper));
